import React from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Play } from 'lucide-react';
import { Badge } from '../ui/badge';

const NewsCard = ({ news, variant = 'default', showCategory = true }) => {
  const formattedDate = format(new Date(news.created_at), 'd MMMM yyyy, HH:mm', { locale: tr });
  
  const categoryLabels = {
    'ekonomi': 'Ekonomi',
    'politika': 'Politika',
    'vizyon-sektor': 'Vizyon Sektör',
    'dunya': 'Dünya',
    'teknoloji': 'Teknoloji',
    'finans': 'Finans',
  };

  const isVideo = news.youtube_url && news.category === 'vizyon-sektor';

  if (variant === 'featured') {
    return (
      <Link 
        to={`/haber/${news.id}`} 
        className="group block"
        data-testid={`featured-news-${news.id}`}
      >
        <div className="relative aspect-[16/9] md:aspect-[21/9] overflow-hidden rounded-lg">
          <img
            src={news.image_url}
            alt={news.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
            {showCategory && (
              <Badge 
                className="mb-3 bg-[#0284C7] hover:bg-[#0369A1] text-white font-semibold text-xs uppercase tracking-wide"
              >
                {categoryLabels[news.category] || news.category}
              </Badge>
            )}
            <h2 className="text-xl md:text-3xl lg:text-4xl font-bold text-white leading-tight mb-3 news-title transition-colors">
              {news.title}
            </h2>
            <p className="text-gray-300 text-sm md:text-base line-clamp-2 max-w-3xl">
              {news.body.substring(0, 150)}...
            </p>
            <p className="text-gray-400 text-xs mt-3">{formattedDate}</p>
          </div>
          {isVideo && (
            <div className="absolute top-4 right-4 bg-black/60 rounded-full p-3">
              <Play className="w-6 h-6 text-white fill-white" />
            </div>
          )}
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link 
        to={`/haber/${news.id}`} 
        className="group flex gap-4 news-card"
        data-testid={`horizontal-news-${news.id}`}
      >
        <div className="relative w-32 h-24 md:w-40 md:h-28 flex-shrink-0 overflow-hidden rounded-lg">
          <img
            src={news.image_url}
            alt={news.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          {isVideo && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
              <Play className="w-8 h-8 text-white fill-white" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0 py-1">
          {showCategory && (
            <span className="text-xs font-semibold uppercase tracking-wide text-[#0284C7] mb-1 block">
              {categoryLabels[news.category] || news.category}
            </span>
          )}
          <h3 className="text-sm md:text-base font-bold text-gray-900 leading-snug line-clamp-2 news-title transition-colors">
            {news.title}
          </h3>
          <p className="text-gray-500 text-xs mt-2">{formattedDate}</p>
        </div>
      </Link>
    );
  }

  // Default card
  return (
    <Link 
      to={`/haber/${news.id}`} 
      className="group block news-card"
      data-testid={`news-card-${news.id}`}
    >
      <div className="relative aspect-video overflow-hidden rounded-lg mb-3">
        <img
          src={news.image_url}
          alt={news.title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        {isVideo && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="bg-white/90 rounded-full p-3">
              <Play className="w-6 h-6 text-[#0F172A] fill-[#0F172A]" />
            </div>
          </div>
        )}
      </div>
      <div>
        {showCategory && (
          <span className="text-xs font-semibold uppercase tracking-wide text-[#0284C7] mb-2 block">
            {categoryLabels[news.category] || news.category}
          </span>
        )}
        <h3 className="text-base md:text-lg font-bold text-gray-900 leading-snug line-clamp-3 news-title transition-colors">
          {news.title}
        </h3>
        <p className="text-gray-500 text-xs mt-2">{formattedDate}</p>
      </div>
    </Link>
  );
};

export default NewsCard;
